// Lógica pura de "Mi semana" en el dashboard del miembro: qué ocurrencia cae en
// qué fecha de la semana, y si ya se hizo.
//
// El coach programa cada entreno para un día, pero el cliente puede abrirlo
// cualquier otro día (se le atravesó algo el martes → lo hace el miércoles).
// Por eso el "completado" se decide por OCURRENCIA (el log apunta al routine_id
// de esa fila), no por "hay un log con fecha de hoy". El log guarda la fecha
// real en performed_at, que es lo que ve el coach y lo que agrupa Progreso.

import type { Routine, WorkoutLog } from "../types"
import { toISODate } from "./routineSchedule"

export interface Week {
  start: Date // lunes
  end: Date // domingo
  startISO: string
  endISO: string
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d)
  out.setDate(out.getDate() + n)
  return out
}

// Semana lunes–domingo que contiene `anchor`, desplazada `offset` semanas.
export function weekOf(anchor: Date, offset = 0): Week {
  const start = new Date(anchor)
  start.setHours(0, 0, 0, 0)
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7) + offset * 7)
  const end = addDays(start, 6)
  return { start, end, startISO: toISODate(start), endISO: toISODate(end) }
}

// Fecha (YYYY-MM-DD) en la que cae una rutina dentro de la semana. Espejo de
// routineOnDay: con scheduled_date es esa fecha; sin ella (legacy, semanal por
// día de semana) es el día correspondiente de ESTA semana. null si no se
// puede ubicar (sin fecha ni día).
export function occurrenceDate(routine: Routine, week: Week): string | null {
  if (routine.scheduled_date) return routine.scheduled_date.slice(0, 10)
  if (routine.scheduled_day == null) return null
  return toISODate(addDays(week.start, (routine.scheduled_day + 6) % 7))
}

// Log que marca esta ocurrencia como hecha, si existe.
//   - Ocurrencia con fecha (fila de una serie): cualquier log de ese routine_id,
//     sin importar el día en que se hizo — la fila ES la ocurrencia.
//   - Rutina legacy (se repite cada semana con el mismo id): solo cuenta un log
//     dentro de la semana mostrada, si no quedaría "completada" para siempre.
export function completionLog(
  routine: Routine,
  logs: WorkoutLog[],
  week: Week,
): WorkoutLog | undefined {
  return logs.find((l) => {
    if (l.routine_id !== routine.id) return false
    if (routine.scheduled_date) return true
    const day = l.performed_at.slice(0, 10)
    return day >= week.startISO && day <= week.endISO
  })
}

export type OccurrenceStatus = "completed" | "today" | "overdue" | "upcoming"

export interface Occurrence {
  routine: Routine
  date: string // YYYY-MM-DD
  log?: WorkoutLog
  status: OccurrenceStatus
}

// Ocurrencias de la semana ordenadas por fecha y hora, con su estado. Las
// rutinas sin fecha ni día se omiten (no hay dónde ponerlas; el backend ya
// impide crearlas, ver Routine#debe_tener_fecha_o_dia).
export function weekOccurrences(
  routines: Routine[],
  logs: WorkoutLog[],
  week: Week,
  todayISO: string,
): Occurrence[] {
  const out: Occurrence[] = []
  for (const routine of routines) {
    const date = occurrenceDate(routine, week)
    if (!date) continue
    const log = completionLog(routine, logs, week)
    const status: OccurrenceStatus = log
      ? "completed"
      : date === todayISO
        ? "today"
        : date < todayISO
          ? "overdue"
          : "upcoming"
    out.push({ routine, date, log, status })
  }
  return out.sort(
    (a, b) =>
      a.date.localeCompare(b.date) ||
      (a.routine.start_hour ?? 24) - (b.routine.start_hour ?? 24) ||
      a.routine.name.localeCompare(b.routine.name),
  )
}
